import React, { Component, useEffect, useState } from 'react';
import { array, arrayOf, bool, func, object, oneOf, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import classNames from 'classnames';
// Removed heavy lodash import - using native implementations instead

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { fetchCommission } from '../../util/api';

import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import { parse, redirectToStore } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import {
  manageDisableScrolling,
  isScrollingDisabled,
  setDOMLoaded,
  setFirstLoaded,
} from '../../ducks/ui.duck';
import { isListingHidden } from '../../util/listingsHelpers';

import {
  Button,
  H3,
  H5,
  IconSettings,
  Modal,
  ModalInMobile,
  NamedLink,
  Page,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import { setActiveListing } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
// Lazy load SearchMap only when needed
const SearchMap = React.lazy(() => import('./SearchMap/SearchMap'));
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import { fetchCreatorsAndStoresWithReviews } from '../../ducks/user.duck';

import topoImage from './../../assets/topo.jpg';
import { isNotEmptyString } from '../../util/string';
import {
  getSubcategoryCategory,
  normalizeLegacySubcategorySlug,
  STORE_SIDEBAR_CATEGORIES,
} from '../../util/storeTaxonomy';
import {
  SEARCH_NAVIGATION_CATEGORY_ITEMS,
  SEARCH_NAVIGATION_SUBCATEGORY_ITEMS,
} from '../../util/storeTaxonomyNavigation';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

// Memoized FilterComponent to prevent unnecessary re-renders
const MemoizedFilterComponent = React.memo(FilterComponent);

// Move static data outside component to prevent recreation on every render
const STATIC_CATEGORIES = STORE_SIDEBAR_CATEGORIES.map(category => ({
  label: category.label,
  category: category.option,
}));

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearchMapOpenOnMobile: false,
      isSearchMapOpenOnDesktop: false,
      isFiltersModalOpen: false,
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      showLeftArrow: false,
      showRightArrow: false,
    };

    this.categoriesScrollRef = React.createRef();

    // Cache for expensive computations
    this._computationCache = {
      lastProps: null,
      lastParams: null,
      categorySelected: null,
      customFilters: null,
      availablePrimaryFilters: null,
      availableFilters: null,
    };

    // Optimize debounced callback
    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);

    // Bind methods to preserve context and prevent recreation
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.onToggleMobileModal = this.onToggleMobileModal.bind(this);
    this.onToggleFiltersModal = this.onToggleFiltersModal.bind(this);

    // Filter functions
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // Category scroll arrows
    this.checkArrowVisibility = this.checkArrowVisibility.bind(this);
  }

  checkArrowVisibility() {
    const container = this.categoriesScrollRef.current;
    if (!container) return;

    const isAtStart = container.scrollLeft <= 5;
    const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;

    this.setState({
      showLeftArrow: !isAtStart,
      showRightArrow: !isAtEnd,
    });
  }

  componentDidMount() {
    setTimeout(() => this.checkArrowVisibility(), 10);

    const container = this.categoriesScrollRef.current;
    if (container) {
      container.addEventListener('scroll', this.checkArrowVisibility);
    }
    window.addEventListener('resize', this.checkArrowVisibility);
  }

  componentWillUnmount() {
    const container = this.categoriesScrollRef.current;
    if (container) {
      container.removeEventListener('scroll', this.checkArrowVisibility);
    }
    window.removeEventListener('resize', this.checkArrowVisibility);
  }

  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;

    const routes = this.props.routeConfiguration;
    const searchPagePath = pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};

      // parse query parameters, including a custom attribute named category
      const { address, bounds, mapSearch, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, listingFieldsConfig, defaultFiltersConfig, dropNonFilterParams),
      };

      history.push(createResourceLocatorString('SearchPage', routes, {}, searchParams));
    }
  }

  onToggleMobileModal() {
    this.setState({ isMobileModalOpen: !this.state.isMobileModalOpen });
  }

  onToggleFiltersModal() {
    this.setState({ isFiltersModalOpen: !this.state.isFiltersModalOpen });
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig
    );

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        return {
          currentQueryParams: {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            address,
            bounds,
          },
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(
            searchParams,
            listingFieldsConfig,
            defaultFiltersConfig,
            sortConfig
          );
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  // Cached computation methods for performance optimization
  getCategorySelected(hasCategory, params) {
    const cacheKey = `${hasCategory}_${params?.category}`;
    if (this._computationCache.lastParams !== cacheKey) {
      this._computationCache.lastParams = cacheKey;
      this._computationCache.categorySelected = hasCategory
        ? STATIC_CATEGORIES.find(cat => cat.category == params.category)
        : null;
    }
    return this._computationCache.categorySelected;
  }

  getCustomFilters(listingFieldsConfig, activeListingTypes) {
    // Use lightweight cache key instead of expensive JSON.stringify
    const configKey = `${listingFieldsConfig?.length || 0}-${activeListingTypes?.length || 0}`;
    if (
      !this._computationCache.customFilters ||
      this._computationCache.lastConfigKey !== configKey
    ) {
      this._computationCache.lastConfigKey = configKey;
      const [primary, secondary] = groupListingFieldConfigs(
        listingFieldsConfig,
        activeListingTypes
      );
      this._computationCache.customFilters = {
        customPrimaryFilters: primary,
        customSecondaryFilters: secondary,
      };
    }
    return this._computationCache.customFilters;
  }

  getAvailablePrimaryFilters(customPrimaryFilters, categorySelected, defaultFilters) {
    // Use lightweight cache key instead of expensive JSON.stringify
    const filtersKey = `${customPrimaryFilters?.length || 0}-${categorySelected?.category ||
      'none'}-${defaultFilters?.length || 0}`;

    if (
      !this._computationCache.availablePrimaryFilters ||
      this._computationCache.lastFiltersKey !== filtersKey
    ) {
      this._computationCache.lastFiltersKey = filtersKey;

      // Optimized filtering without expensive cloneDeep
      const filteredPrimary = customPrimaryFilters
        .filter(f => {
          if (categorySelected) {
            return f.key !== 'category';
          }
          return true;
        })
        .map(f => {
          if (f.key === 'subcategory' && categorySelected) {
            // Only clone and modify if we actually need to change enumOptions
            return {
              ...f,
              enumOptions: (f.enumOptions || []).filter(
                item => getSubcategoryCategory(item.option) === categorySelected.category
              ),
            };
          }
          return f; // Return original reference if no changes needed
        });

      this._computationCache.availablePrimaryFilters = [...filteredPrimary, ...defaultFilters];
    }
    return this._computationCache.availablePrimaryFilters;
  }

  getAvailableFilters(customPrimaryFilters, defaultFilters, customSecondaryFilters) {
    // Use lightweight cache key instead of expensive JSON.stringify
    const allFiltersKey = `${customPrimaryFilters?.length || 0}-${defaultFilters?.length ||
      0}-${customSecondaryFilters?.length || 0}`;

    if (
      !this._computationCache.availableFilters ||
      this._computationCache.lastAllFiltersKey !== allFiltersKey
    ) {
      this._computationCache.lastAllFiltersKey = allFiltersKey;
      this._computationCache.availableFilters = [
        ...customPrimaryFilters,
        ...defaultFilters,
        ...customSecondaryFilters,
      ];
    }
    return this._computationCache.availableFilters;
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Optimize re-renders by checking only relevant props/state changes
    const relevantProps = [
      'listings',
      'mapListings',
      'pagination',
      'searchInProgress',
      'searchListingsError',
      'searchParams',
      'activeListingId',
      'location',
    ];

    const relevantState = [
      'isSearchMapOpenOnMobile',
      'isSearchMapOpenOnDesktop',
      'isFiltersModalOpen',
      'isMobileModalOpen',
      'currentQueryParams',
      'isSecondaryFiltersOpen',
    ];

    // Check if any relevant props changed
    for (let prop of relevantProps) {
      if (this.props[prop] !== nextProps[prop]) {
        return true;
      }
    }

    // Check if any relevant state changed
    for (let state of relevantState) {
      if (this.state[state] !== nextState[state]) {
        return true;
      }
    }

    return false;
  }

  render() {
    const {
      intl,
      listings,
      mapListings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      activeListingId,
      onActivateListing,
      routeConfiguration,
      config,
      params = {},
    } = this.props;

    const hasCategory = params?.category && isNotEmptyString(params.category);

    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config),
      hasCategory
    );

    const validQueryParams = validFilterParams(
      searchParamsInURL,
      listingFieldsConfig,
      defaultFiltersConfig,
      false
    );

    const isWindowDefined = typeof window !== 'undefined';
    const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);
    const shouldShowSearchMapDesktop = !isMobileLayout && this.state.isSearchMapOpenOnDesktop;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const defaultFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => f.key !== 'keywords')
      : defaultFiltersConfig;

    // Use cached methods instead of hooks
    const { customPrimaryFilters, customSecondaryFilters } = this.getCustomFilters(
      listingFieldsConfig,
      activeListingTypes
    );

    const categorySelected = this.getCategorySelected(hasCategory, params);

    const availablePrimaryFilters = this.getAvailablePrimaryFilters(
      customPrimaryFilters,
      categorySelected,
      defaultFilters
    );

    const availableFilters = this.getAvailableFilters(
      customPrimaryFilters,
      defaultFilters,
      customSecondaryFilters
    );

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = validFilterParams(
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

    // Selected aka active secondary filters
    const selectedSecondaryFilters = hasSecondaryFilters
      ? validFilterParams(validQueryParams, customSecondaryFilters, [])
      : {};
    const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
          },
          selectedSecondaryFiltersCount,
        }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;

    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const sortBy = (mode, classStr = null) => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          classStr={classStr}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        isAuthenticated={this.props.isAuthenticated}
        currentUser={this.props.currentUser}
      />
    );

    const { bounds, origin } = searchParamsInURL || {};
    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    const totalFilters = Object.values(selectedFilters).reduce((acc, value) => {
      return acc + value.split(',').length;
    }, 0);

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // Helper functions for category selection
    const isCategorySelected = cat => {
      if (selectedFilters?.pub_category && isNotEmptyString(selectedFilters.pub_category)) {
        let categoriesArr = selectedFilters.pub_category.split(',');
        return categoriesArr.includes(cat.category);
      }
      return false;
    };

    const isSubCategorySelected = cat => {
      const urlParams = new URLSearchParams(location.search);
      return normalizeLegacySubcategorySlug(urlParams.get('pub_subcategory')) === cat.option;
    };

    // Categories container
    const categoriesContainer = (
      <div className={css.categoriesContainer}>
        <div>
          <div>
            {!categorySelected ? (
              <div>"The best deals from the best hunting and outdoor shops"</div>
            ) : (
              <>
                <div>{categorySelected.label}</div>
                <div></div>
              </>
            )}
            {!categorySelected ? (
              <div>
                Every listing comes from a Bowtackle certified seller. Interested in becoming a
                seller?{' '}
                <a href="/become-a-seller" className={css.providerSignupLink}>
                  Apply here
                </a>
                .
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );

    // Filter components
    const filtersComponent = availableFilters.map(config => {
      return (
        <MemoizedFilterComponent
          key={`SearchFiltersMobile.${config.scope || 'built-in'}.${config.key}`}
          idPrefix="SearchFiltersMobile"
          config={config}
          marketplaceCurrency={marketplaceCurrency}
          urlQueryParams={validQueryParams}
          initialValues={initialValues(this.props, this.state.currentQueryParams)}
          getHandleChangedValueFn={this.getHandleChangedValueFn}
          intl={intl}
          liveEdit
          showAsPopup={false}
        />
      );
    });

    // Categories navigation
    const categoriesNavigation = (
      <div className={css.categoriesBar}>
        {this.state.showLeftArrow && (
          <button
            className={css.scrollArrowLeft}
            onClick={() => {
              const el = this.categoriesScrollRef.current;
              if (el) {
                el.scrollBy({ left: -300, behavior: 'smooth' });
                setTimeout(() => this.checkArrowVisibility(), 100);
              }
            }}
          >
            ‹
          </button>
        )}
        <div className={css.categoriesScroll} ref={this.categoriesScrollRef}>
          {[...SEARCH_NAVIGATION_CATEGORY_ITEMS, ...SEARCH_NAVIGATION_SUBCATEGORY_ITEMS].map(
            (item, index) => {
              const isCategory = !!item.category;

              return (
                <NamedLink
                  key={index}
                  className={classNames(css.categoryLink, {
                    [css.categoryContainerLinkSelected]: isCategorySelected(item),
                    [css.subcategoryLinkSelected]: isSubCategorySelected(item),
                  })}
                  name="SearchPage"
                  to={{
                    search: isCategory
                      ? `pub_category=${item.category}`
                      : `pub_category=${item.parentCategory}&pub_subcategory=${item.option}`,
                  }}
                >
                  <div className={css.categoryIcon}>
                    <img src={item.img} alt={item.label} style={item.style} />
                  </div>
                  <p className={css.categoryNameLabel}>{item.label}</p>
                  <div className={css.categoryLabel}></div>
                </NamedLink>
              );
            }
          )}
        </div>
        {this.state.showRightArrow && (
          <button
            className={css.scrollArrowRight}
            onClick={() => {
              const el = this.categoriesScrollRef.current;
              if (el) {
                el.scrollBy({ left: 300, behavior: 'smooth' });
                setTimeout(() => this.checkArrowVisibility(), 100);
              }
            }}
          >
            ›
          </button>
        )}
        <div className={css.settingsBtnHandler}>
          <Button type="button" onClick={this.onToggleFiltersModal}>
            {totalFilters > 0 ? <span>{totalFilters}</span> : null}
            <IconSettings />
          </Button>
        </div>
      </div>
    );

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer
          rootClassName={topbarClasses}
          currentPage="SearchPage"
          currentSearchParams={urlQueryParams}
          showFilters={this.onToggleFiltersModal}
        />
        {categoriesContainer}
        {categoriesNavigation}
        <Modal
          isOpen={this.state.isFiltersModalOpen}
          onClose={this.onToggleFiltersModal}
          onManageDisableScrolling={onManageDisableScrolling}
          id="SearchPage.filtersModal"
        >
          <div className={css.modalHeadingWrapper}>
            <span className={css.modalHeading}>
              {intl.formatMessage({ id: 'SearchFiltersMobile.heading' })}
            </span>
            <button className={css.resetAllButton} onClick={this.resetAll}>
              <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
            </button>
          </div>
          <div className={css.filtersWrapper}>
            {sortBy('mobile', css.mobileSort)}
            {filtersComponent}
          </div>

          <div className={css.showListingsContainer}>
            <Button className={css.showListingsButton} onClick={this.onToggleFiltersModal}>
              {intl.formatMessage(
                { id: 'SearchFiltersMobile.showListings' },
                { count: totalItems }
              )}
            </Button>
          </div>
        </Modal>

        <div
          className={classNames(css.container, {
            [css.fullContainer]: !isMobileLayout && !shouldShowSearchMapDesktop,
            [css.hideForMap]: !isMobileLayout && shouldShowSearchMapDesktop,
          })}
        >
          <div className={css.searchResultContainer}>
            <SearchFiltersMobile
              isMobileModalOpen={this.state.isMobileModalOpen}
              className={css.searchFiltersMobileMap}
              urlQueryParams={validQueryParams}
              sortByComponent={sortBy('mobile')}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onMapIconClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
              onManageDisableScrolling={onManageDisableScrolling}
              onOpenModal={this.onOpenMobileModal}
              onCloseModal={this.onCloseMobileModal}
              resetAll={this.resetAll}
              selectedFiltersCount={selectedFiltersCountForMobile}
              noResultsInfo={noResultsInfo}
              isMapVariant
              showFilters={false}
              showFound={false}
            >
              {sortBy('mobile', css.mobileSort)}
              {filtersComponent}
            </SearchFiltersMobile>
            {/* <MainPanelHeader
              className={css.mainPanelMapVariant}
              sortByComponent={sortBy('desktop')}
              isSortByActive={false}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              noResultsInfo={noResultsInfo}
            >
              <SearchFiltersPrimary {...propsForSecondaryFiltersToggle}>
                {availablePrimaryFilters.map(config => {
                  return (
                    <MemoizedFilterComponent
                      key={`SearchFiltersPrimary.${config.scope || 'built-in'}.${config.key}`}
                      idPrefix="SearchFiltersPrimary"
                      config={config}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      showAsPopup
                      contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                    />
                  );
                })}
                {sortConfig.active ? (
                  <div className={css.sortyByWrapper}>
                    <span className={css.sortyBy}>
                      <FormattedMessage id="MainPanelHeader.sortBy" />
                    </span>
                    {sortBy('desktop')}
                  </div>
                ) : null}
              </SearchFiltersPrimary>
            </MainPanelHeader> */}
            {isSecondaryFiltersOpen ? (
              <div className={classNames(css.searchFiltersPanel)}>
                <SearchFiltersSecondary
                  urlQueryParams={validQueryParams}
                  listingsAreLoaded={listingsAreLoaded}
                  applyFilters={this.applyFilters}
                  cancelFilters={this.cancelFilters}
                  resetAll={this.resetAll}
                  onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
                >
                  {customSecondaryFilters.map(config => {
                    return (
                      <MemoizedFilterComponent
                        key={`SearchFiltersSecondary.${config.scope || 'built-in'}.${config.key}`}
                        idPrefix="SearchFiltersSecondary"
                        config={config}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersSecondary>
              </div>
            ) : (
              <div
                className={classNames(css.listingsForMapVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={(listings || []).filter(
                    l => !isListingHidden(l.id?.uuid) && !l?.attributes?.publicData?.isManufacturer
                  )}
                  stores={this.props.stores}
                  creators={this.props.creators}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  setActiveListing={onActivateListing}
                  intl={intl}
                  toggleMap={() => this.setState({ isSearchMapOpenOnDesktop: true })}
                  isMapVariant
                  commissionData={this.props.commissionData}
                  commissionLoading={this.props.commissionLoading}
                  commissionError={this.props.commissionError}
                />
              </div>
            )}
          </div>
          <ModalInMobile
            className={css.mapPanel}
            id="SearchPage.map"
            isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
            onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
            showAsModalMaxWidth={MODAL_BREAKPOINT}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.mapWrapper} data-testid="searchMapContainer">
              {(isMobileLayout && shouldShowSearchMap) ||
              (!isMobileLayout && shouldShowSearchMapDesktop) ? (
                <React.Suspense
                  fallback={<div className={css.mapLoadingPlaceholder}>Loading map...</div>}
                >
                  <SearchMap
                    reusableContainerClassName={css.map}
                    activeListingId={activeListingId}
                    bounds={bounds}
                    center={origin}
                    isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                    location={location}
                    listings={(mapListings || listings || []).filter(
                      l =>
                        !isListingHidden(l.id?.uuid) && !l?.attributes?.publicData?.isManufacturer
                    )}
                    stores={this.props.stores}
                    onMapMoveEnd={this.onMapMoveEnd}
                    onCloseAsModal={() => {
                      onManageDisableScrolling('SearchPage.map', false);
                    }}
                    messages={intl.messages}
                  />
                  <Button
                    type="button"
                    className={css.toggleMap}
                    onClick={() => {
                      this.setState({ isSearchMapOpenOnDesktop: false });
                      this.setState({ isSearchMapOpenOnMobile: false });
                    }}
                  >
                    {intl.formatMessage({ id: 'SearchPage.toggleList' })}
                  </Button>
                </React.Suspense>
              ) : null}
            </div>
          </ModalInMobile>
        </div>
        <div className={css.searchFooter}>
          <FooterContainer />
        </div>
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  mapListings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  activeListingId: null,
};

SearchPageComponent.propTypes = {
  listings: array,
  mapListings: array,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

// Custom hook to fetch commission data from Sharetribe Console
const useCommissionData = () => {
  const [state, setState] = useState({
    commissionData: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    let mounted = true;
    setState(prev => ({ ...prev, loading: true }));

    fetchCommission()
      .then(response => {
        if (mounted) {
          setState({
            commissionData: response.data,
            loading: false,
            error: null,
          });
        }
      })
      .catch(err => {
        if (mounted) {
          console.error('Failed to fetch commission data for SearchPage:', err);
          setState({
            commissionData: null,
            loading: false,
            error: err,
          });
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  return state;
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const {
    commissionData,
    loading: commissionLoading,
    error: commissionError,
  } = useCommissionData();

  useEffect(() => {
    props.handleDomLoaded();
    let isMounted = true;

    const maybeRedirectToHostedStore = async () => {
      const { shouldRedirect } = await redirectToStore({
        redirect: false,
        history,
        routeConfiguration,
      });
      if (isMounted && shouldRedirect) {
        await redirectToStore({ redirect: true, history, routeConfiguration });
      }
    };

    maybeRedirectToHostedStore();

    return () => {
      isMounted = false;
    };
  }, [history, props.handleDomLoaded, routeConfiguration]);

  useEffect(() => {
    if (props.domLoaded && (props?.listings || []).length && !props.firstLoaded) {
      props.handleFirstLoaded();
      const creatorsId = (props?.listings || [])
        .map(l => l?.attributes?.publicData?.creatorId)
        .filter(l => l);
      props.handleCreatorsAndStores(creatorsId);
    }
  }, [props.domLoaded, props.listings?.length, props.firstLoaded]); // More specific dependencies

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      commissionData={commissionData}
      commissionLoading={commissionLoading}
      commissionError={commissionError}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    mapListingsIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  } = state.SearchPage;

  const { stores, creators, currentUser } = state.user;

  const { domLoaded, firstLoaded } = state.ui;

  const { isAuthenticated } = state.auth;

  const listings = getListingsById(state, currentPageResultIds);
  const mapListings = getListingsById(state, mapListingsIds);

  return {
    listings,
    mapListings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    stores,
    creators,
    currentUser,
    isAuthenticated,
    domLoaded,
    firstLoaded,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
  handleDomLoaded: () => dispatch(setDOMLoaded()),
  handleFirstLoaded: () => dispatch(setFirstLoaded()),
  handleCreatorsAndStores: creatorsId => dispatch(fetchCreatorsAndStoresWithReviews(creatorsId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
